import { useState } from 'react';
import { Lock, Leaf, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function CompletePasswordSetup() {
  const { checkAuth } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/complete-password-setup', { new_password: password });
      toast.success('Password set! Welcome to your Farm Partner Portal.');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center" data-testid="complete-password-setup-page">
      <div className="w-full max-w-md px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-forest rounded-full flex items-center justify-center mx-auto mb-4"><Leaf className="w-6 h-6 text-cream" /></div>
          <h1 className="font-playfair text-3xl font-bold text-dark">Create Your Permanent Password</h1>
          <p className="font-manrope text-dark/60 mt-2">You're using a temporary password. Set a permanent one to continue to your Farm Partner Dashboard.</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-forest/10 shadow-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="new-password" className="font-manrope text-dark">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/40" />
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 pr-12 bg-cream border-forest/20"
                  data-testid="complete-setup-new-password"
                  minLength={6}
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-dark/40 hover:text-dark">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="font-manrope text-dark">Confirm New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/40" />
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 bg-cream border-forest/20"
                  data-testid="complete-setup-confirm-password"
                  minLength={6}
                  required
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="complete-setup-submit">
              {loading ? 'Setting password…' : 'Create Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
